import { UploadStatus } from "@/shared/enums/upload-status";
import { RootState } from "@/store";
import { artifactUploaderActions } from "@/store/artifactUploader/artifactUploaderAction";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

/**
 * Estados que indican que un upload ha finalizado (éxito o error)
 */
const FINISHED_UPLOAD_STATUSES: UploadStatus[] = [
	UploadStatus.COMPLETED,
	UploadStatus.UPLOADING_ERROR,
	UploadStatus.ANALYZING_ERROR,
];

/**
 * Extrae el datasetId de una ruta
 */
const extractDatasetId = (path: string): string | null => {
	const secondStepMatch = path.match(/\/second-step\/([^\/]+)/);
	const catalogueMatch = path.match(/\/catalogue\/([^\/]+)/);
	return secondStepMatch?.[1] || catalogueMatch?.[1] || null;
};

/**
 * Verifica si una ruta corresponde a second-step
 */
const isSecondStepRoute = (path: string): boolean => {
	return path.includes("/second-step/");
};

/**
 * Verifica si una ruta corresponde a catalogue pero NO es second-step
 */
const isCatalogueNonSecondStepRoute = (path: string): boolean => {
	// Verifica que sea una ruta de catalogue y NO sea second-step (que termina en /2)
	return (
		path.includes("/catalogue/") &&
		!path.includes("/second-step/") &&
		!path.endsWith("/2")
	);
};

/**
 * Hook para manejar la navegación entre rutas y limpiar uploads
 */
export const useRouteNavigation = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { activeUploads } = useSelector((state: RootState) => state.uploadFile);
	const [previousPath, setPreviousPath] = useState<string>("");

	useEffect(() => {
		const previousDatasetId = extractDatasetId(previousPath);
		const currentDatasetId = extractDatasetId(location.pathname);

		const wasInSecondStep = isSecondStepRoute(previousPath);
		const isNowInCatalogue = isCatalogueNonSecondStepRoute(location.pathname);

		// Si salimos de second-step y vamos al mismo dataset en catalogue
		if (
			wasInSecondStep &&
			isNowInCatalogue &&
			previousDatasetId === currentDatasetId &&
			currentDatasetId
		) {
			// Limpiar archivos completados del dataset actual
			const uploadsToClean = activeUploads.filter(
				(upload) =>
					upload.datasetId === currentDatasetId &&
					FINISHED_UPLOAD_STATUSES.includes(upload.status),
			);

			for (const upload of uploadsToClean) {
				dispatch(
					artifactUploaderActions.removeUpload({
						id: upload.id,
						datasetId: upload.datasetId,
					}),
				);
			}
		}

		setPreviousPath(location.pathname);
	}, [location.pathname, dispatch, activeUploads, previousPath]);
};
